import {
  IDENTITY_OPERATORS_BY_TYPE,
  presenceComparators,
  presetsByType,
  valueComparatorsByType,
  type Filter,
  type FilterComparator,
  type FilterDataType,
} from "@/shared/domain/query/filters";

import type { FilterDefinition } from "./FilterDefinition";

// The filter WIRE model — row variants, comparator/preset vocabulary, the
// `filterSchema` validator — lives in the query domain, twinned with the
// backend's `roboto.query.filters`. Re-exported here so this layer's many
// importers keep one path. What remains below is UI policy: which operators a
// given field OFFERS in the picker, decided off `FilterDefinition` (a UI
// config type), while the domain model says what the wire ACCEPTS.
export * from "@/shared/domain/query/filters";

// Membership fields are the only ones with no nullability axis: an empty tag list is
// not the same as an absent one, so "is set" has nothing to ask. Every other type
// offers the presence pair when its field is `nullable`.
//
// Stated as the exception rather than as a list of the types that qualify, because the
// list form silently omitted `metric` — the operator picker never offered a pair that
// `comparatorsByType` accepted and the session query path implements
// (`_METRIC_COMPARATOR_TO_SUBQUERY_OPERATORS` maps IS_NULL to a NOT EXISTS over the
// metrics table). A new field type now gets the presence axis by default, and opting
// out is a deliberate edit here.
const TYPES_WITHOUT_PRESENCE_AXIS: ReadonlySet<FilterDataType> = new Set([
  "set",
]);

/**
 * The value-bearing comparators a field exposes in the OperatorPicker: the
 * type's value set, plus the presence pair when the field is `nullable`.
 * `string` narrows its value set via the optional `allowedComparators`. `set`
 * has no presence axis, so `nullable` is ignored for it. `identity` derives its
 * set from the principal types the field declares — one `IS_<TYPE>` each, in
 * declared order.
 *
 * The single seam for per-field comparator selection: `OperatorPicker` renders
 * this list and `defaultComparatorFor` reads its head, so the "default is
 * always a visible comparator" invariant holds by construction. Presets are the
 * parallel seam — see {@link effectivePresets}.
 */
export function effectiveComparators(
  definition: FilterDefinition,
): readonly FilterComparator[] {
  if (definition.type === "identity") {
    return definition.options.map(
      (option) => IDENTITY_OPERATORS_BY_TYPE[option.type].comparator,
    );
  }

  const base =
    definition.type === "string" && definition.allowedComparators
      ? definition.allowedComparators
      : valueComparatorsByType[definition.type];

  return definition.nullable &&
    !TYPES_WITHOUT_PRESENCE_AXIS.has(definition.type)
    ? [...base, ...presenceComparators]
    : base;
}

/**
 * The date presets the picker offers, shortest window first.
 *
 * Narrower than the wire vocabulary in `datePresets`, which is the set the backend accepts
 * and must stay in step with it. `LAST_90_DAYS` and `THIS_MONTH` are deliberately absent:
 * a saved filter holding either still parses and still renders its chip, but neither is
 * offered for new filters. Offering policy belongs here rather than in the schema module,
 * which states what is valid rather than what is useful.
 *
 * The hour windows lead because they are measured back from now, so they read the same in
 * every timezone. `TODAY` is anchored to UTC midnight, which makes it a sliver of work for
 * a reader in the Americas during their afternoon.
 */
const OFFERED_DATE_PRESETS = [
  "LAST_3_HOURS",
  "LAST_8_HOURS",
  "LAST_24_HOURS",
  "TODAY",
  "LAST_7_DAYS",
  "LAST_30_DAYS",
] as const satisfies readonly FilterComparator[];

/**
 * The presets a field exposes, mirroring {@link effectiveComparators} for the
 * valueless half of the picker. Dates offer {@link OFFERED_DATE_PRESETS} regardless of the
 * field; `identity` narrows to the principal types the field declares, since an
 * `IS_ANY_<TYPE>` for a type the column never holds would match nothing.
 *
 * An identity field's presets are withheld entirely unless it admits more than
 * one principal type. On a single-type field "any user" is `created_by LIKE
 * 'user:%'` — a predicate every row satisfies, so it would sit beside a
 * specific-identity picker looking like a filter while doing nothing.
 */
export function effectivePresets(
  definition: FilterDefinition,
): readonly FilterComparator[] {
  if (definition.type === "date") {
    return OFFERED_DATE_PRESETS;
  }
  if (definition.type !== "identity") {
    return presetsByType[definition.type];
  }
  if (definition.options.length < 2) {
    return [];
  }
  return definition.options.map(
    (option) => IDENTITY_OPERATORS_BY_TYPE[option.type].preset,
  );
}

/** The comparator a freshly-picked field defaults to — the head of its visible set. */
export function defaultComparatorFor(
  definition: FilterDefinition,
): FilterComparator {
  return effectiveComparators(definition)[0];
}

/**
 * Defaults seeded into `useForm` when the user picks a field. Empty
 * `values` array — the refine fails until the user fills something in,
 * which keeps Add filter disabled. The comparator is the head of the
 * field's visible set (`defaultComparatorFor`), so it is always one the
 * OperatorPicker will show.
 */
export function getDefaultsForVariant(definition: FilterDefinition): Filter {
  // `defaultComparatorFor` returns a comparator valid for `definition.type`,
  // so the assembled (type, comparator) pair always matches one Filter
  // variant; the cast just re-narrows the umbrella types back to the union.
  return {
    type: definition.type,
    field: definition.field,
    comparator: defaultComparatorFor(definition),
    values: [],
  } as Filter;
}
